import React, { useEffect, useRef, useState, useMemo } from 'react';
import RichText from './components/RichText';
import { Link } from 'react-router-dom';
import { FiLinkedin, FiTwitter, FiGithub, FiFacebook, FiInstagram, FiGlobe } from 'react-icons/fi';
import pb from './lib/pocketbase';
import { useSocieteData } from './context/SocieteContext';
import { useTheme } from './context/ThemeContext';
import ModernToc from './components/ModernToc/ModernToc';
import './RecrutementPage.css';
import './RecruitmentComponents.css';

const SocieteLoader = () => {
  const [msgIdx, setMsgIdx] = useState(0);
  const messages = ["Analyse de la société...", "Chiffres clés...", "Histoire & Valeurs...", "Chargement final..."];

  useEffect(() => {
    const timer = setInterval(() => {
      setMsgIdx(prev => (prev + 1) % messages.length);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="recrutement-loader-container">
      <div className="loader-visual">
        <div className="loader-orbit orbit-1"></div>
        <div className="loader-orbit orbit-2"></div>
        <div className="loader-orbit orbit-3"></div>
        <div className="loader-logo-wrapper">
          <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
        </div>
      </div>
      <div className="loader-message-wrapper">
        <p className="loader-text">{messages[msgIdx]}</p>
        <div className="loader-progress-bar">
          <div className="loader-progress-fill"></div>
        </div>
      </div>
    </div>
  );
};

const TabsBlock = ({ items }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const tabsRef = useRef([]);

  useEffect(() => {
    const activeEl = tabsRef.current[activeTab];
    if (activeEl) {
      setIndicatorStyle({
        left: activeEl.offsetLeft,
        width: activeEl.offsetWidth
      });
    }
  }, [activeTab, items]);

  if (!items || items.length === 0) return null;

  return (
    <div className="block-tabs premium-tabs">
      <div className="tabs-header">
        {items.map((item, i) => (
          <button
            key={i}
            ref={el => tabsRef.current[i] = el}
            className={`tab-btn ${activeTab === i ? 'active' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {item.title}
          </button>
        ))}
        <div className="tabs-indicator" style={indicatorStyle} />
      </div>
      <div className="tabs-content-wrapper">
        <div className="tabs-content">
          <RichText content={items[activeTab]?.content} />
        </div>
      </div>
    </div>
  );
};

const BeforeAfterBlock = ({ before, after }) => {
  const [sliderPos, setSliderPos] = useState(50);
  if (!before && !after) return null;

  return (
    <div className="block-before-after">
      <div className="ba-container">
        <div className="ba-image ba-after" style={{ backgroundImage: after ? `url("${after}")` : 'none', backgroundColor: '#f1f5f9', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="ba-image ba-before" style={{ backgroundImage: before ? `url("${before}")` : 'none', clipPath: `inset(0 ${100 - sliderPos}% 0 0)`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <input type="range" min="0" max="100" value={sliderPos} onChange={(e) => setSliderPos(e.target.value)} className="ba-slider" />
        <div className="ba-handle" style={{ left: `${sliderPos}%` }}><div className="ba-handle-line" /><div className="ba-handle-arrows">↔</div></div>
      </div>
    </div>
  );
};

const StatCard = ({ stat }) => {
  const [count, setCount] = useState(0);
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold: 0.1 });
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const targetValue = parseInt(stat.value.replace(/[^0-9]/g, '')) || 0;
    if (targetValue === 0) { setCount(stat.value); return; }
    let start = 0;
    const duration = 2000;
    const increment = targetValue / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= targetValue) { setCount(stat.value); clearInterval(timer); }
      else {
        const suffix = stat.value.replace(/[0-9]/g, '');
        setCount(stat.value.startsWith(suffix) ? suffix + Math.floor(start) : Math.floor(start) + suffix);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isVisible, stat.value]);

  return (
    <div className="stat-card" ref={cardRef} style={{ '--stat-color': stat.color || '#6366f1' }}>
      <div className="stat-icon-wrapper">{stat.icon ? <img src={stat.icon} alt="" /> : <span>📊</span>}</div>
      <div className="stat-content"><div className="stat-value">{count}</div><div className="stat-label">{stat.label}</div></div>
      <div className="stat-glow"></div>
    </div>
  );
};

const LiveWidget = ({ block }) => {
  const containerRef = useRef(null);
  const rootRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!containerRef.current || !isMounted) return;
      setLoading(true);
      try {
        let codeToRun = "";
        let cssToRun = "";
        if (block.mode === 'library' && block.widgetId) {
          const record = await pb.collection('custom_widgets').getOne(block.widgetId);
          codeToRun = record.code;
          cssToRun = record.css;
        } else {
          codeToRun = block.customCode;
        }
        if (!codeToRun || !isMounted) return;
        const oldStyle = document.getElementById(`style-${block.id}`);
        if (oldStyle) oldStyle.remove();
        if (cssToRun) {
          const style = document.createElement('style');
          style.id = `style-${block.id}`;
          style.innerHTML = cssToRun;
          document.head.appendChild(style);
        }
        const compiled = window.Babel.transform(codeToRun, { presets: ['react'] }).code;
        const render = (Comp) => {
          if (containerRef.current && isMounted) {
            import('react-dom/client').then(({ createRoot }) => {
              if (!rootRef.current) rootRef.current = createRoot(containerRef.current);
              rootRef.current.render(Comp);
            });
          }
        };
        const evalCode = `(function(render, React, useState, useEffect, useRef, theme) { ${compiled} })`;
        eval(evalCode)(render, window.React, window.useState, window.useEffect, window.useRef, theme);
      } catch (err) { if (isMounted) setError(err.message); }
      finally { if (isMounted) setLoading(false); }
    };
    run();
    return () => {
      isMounted = false;
      if (rootRef.current) { rootRef.current.unmount(); rootRef.current = null; }
      const s = document.getElementById(`style-${block.id}`); if (s) s.remove();
    };
  }, [block, theme]);

  if (error) return <div className="alert alert-danger">Error: {error}</div>;
  return <div className={`dynamic-widget-container theme-${block.themeOverride || theme}`} style={{ position: 'relative', minHeight: '50px' }}>{loading && <div style={{ position: 'absolute', top: 0, right: 0, fontSize: '0.6rem' }}>Loading...</div>}<div ref={containerRef} /></div>;
};

const HtmlBlock = ({ block }) => {
  const containerRef = useRef(null);
  const [content, setContent] = useState('');
  const { theme: siteTheme } = useTheme();

  useEffect(() => {
    const load = async () => {
      if (block.mode === 'library' && block.htmlId) {
        const record = await pb.collection('custom_widgets').getOne(block.htmlId);
        setContent(record.code);
      } else { setContent(block.htmlCode || ''); }
    };
    load();
  }, [block]);

  useEffect(() => {
    if (!containerRef.current || !content) return;
    if (block.containerType === 'iframe') {
      const iframe = containerRef.current.querySelector('iframe');
      if (iframe) {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(`<html><body class="theme-${block.themeOverride || siteTheme}">${content}<script>const update=()=>{window.parent.postMessage({height:document.documentElement.scrollHeight,id:'${block.id}'},'*')};window.onload=update;window.onresize=update;setInterval(update,1000);</script></body></html>`);
        doc.close();
      }
      return;
    }
    const range = document.createRange();
    range.selectNode(containerRef.current);
    const frag = range.createContextualFragment(content);
    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(frag);
    containerRef.current.querySelectorAll('script').forEach(s => {
      const n = document.createElement('script');
      Array.from(s.attributes).forEach(a => n.setAttribute(a.name, a.value));
      n.appendChild(document.createTextNode(s.innerHTML));
      s.parentNode.replaceChild(n, s);
    });
  }, [content, block, siteTheme]);

  if (block.containerType === 'iframe') return <div ref={containerRef}><iframe id={`iframe-${block.id}`} title="html" style={{ width: '100%', border: 'none', transition: 'height 0.2s' }} /></div>;
  if (block.containerType === 'none') return <div ref={containerRef} style={{ display: 'contents' }} />;
  return <div ref={containerRef} className={`html-block-container theme-${block.themeOverride || siteTheme}`} />;
};

const GalleryBlock = ({ images }) => {
  const [selectedIdx, setSelectedIdx] = useState(null);

  if (!images || images.length === 0) return null;

  const next = (e) => {
    e.stopPropagation();
    setSelectedIdx((selectedIdx + 1) % images.length);
  };

  const prev = (e) => {
    e.stopPropagation();
    setSelectedIdx((selectedIdx - 1 + images.length) % images.length);
  };

  return (
    <>
      <div className="block-gallery" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        {images.map((img, i) => (
          <div key={i} className="gallery-item" onClick={() => setSelectedIdx(i)}>
            <img src={img.url} alt={img.caption || ''} loading="lazy" style={{ width: '100%', borderRadius: '8px', cursor: 'pointer' }} />
            {img.caption && (
              <div className="gallery-caption-overlay">
                <span>{img.caption}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {selectedIdx !== null && (
        <div className="lightbox-overlay" onClick={() => setSelectedIdx(null)}>
          <button className="lightbox-close" onClick={() => setSelectedIdx(null)}>×</button>

          <div className="lightbox-container" onClick={e => e.stopPropagation()}>
            {images.length > 1 && (
              <button className="lightbox-nav prev" onClick={prev}>‹</button>
            )}

            <div className="lightbox-content">
              <img src={images[selectedIdx].url} alt={images[selectedIdx].caption || ''} />
              {images[selectedIdx].caption && (
                <div className="lightbox-caption">{images[selectedIdx].caption}</div>
              )}
            </div>

            {images.length > 1 && (
              <button className="lightbox-nav next" onClick={next}>›</button>
            )}
          </div>

          <div className="lightbox-counter">{selectedIdx + 1} / {images.length}</div>
        </div>
      )}
    </>
  );
};

export default function SocietePage() {
  const { categories, sections, hero: heroFromContext, loading: dataLoading } = useSocieteData();
  const { theme } = useTheme();
  const [activeId, setActiveId] = useState('');

  // Handle iframe height messages
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data && e.data.height && e.data.id) {
        const iframe = document.getElementById(`iframe-${e.data.id}`);
        if (iframe) {
          iframe.style.height = `${e.data.height}px`;
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Expose React and common hooks to window for custom widgets
  useEffect(() => {
    window.React = React;
    window.useState = useState;
    window.useEffect = useEffect;
    window.useRef = useRef;
  }, []);

  // Use context data or default
  const heroData = heroFromContext || {
    title: 'Société',
    subtitle: 'Une agence digitale à taille humaine.',
    buttons: []
  };

  // Refs for layout and ScrollSpy
  const mobileTocToggleRef = useRef(null);
  const headerHeightRef = useRef(84);
  const mobileToggleOriginalTopRef = useRef(null);
  const containerRef = useRef(null);
  const endRef = useRef(null);

  const [mobileToggleTop, setMobileToggleTop] = useState(84);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [mobileToggleFixed, setMobileToggleFixed] = useState(false);
  const [tocTop, setTocTop] = useState(98);

  const tocData = useMemo(() => categories.map(cat => ({
    id: cat.slug,
    title: cat.title,
    items: sections
      .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
      .filter(s => s.is_published === true)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(s => ({ id: s.slug, title: s.title }))
  })), [categories, sections]);


  useEffect(() => {
    if (dataLoading || sections.length === 0) return;

    // Collect all IDs (sections and categories)
    const catIds = categories.map(c => c.slug).filter(Boolean);
    const secIds = sections.map(s => s.slug).filter(Boolean);
    const allIds = [...catIds, ...secIds];

    const els = allIds.map((id) => document.getElementById(id)).filter(Boolean);

    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { root: null, rootMargin: '-140px 0px -50% 0px', threshold: [0.1, 0.5, 0.9] }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [dataLoading, sections, categories]);

  // Mobile Toggle Sticky Logic
  useEffect(() => {
    const header = document.querySelector('.header');
    headerHeightRef.current = header?.offsetHeight || 84;
    setMobileToggleTop(headerHeightRef.current);
    setTocTop((header?.offsetHeight || 84) + 14);

    const onResize = () => {
      const h = (document.querySelector('.header')?.offsetHeight) || 84;
      headerHeightRef.current = h;
      setMobileToggleTop(h);
      setTocTop(h + 14);
    };
    window.addEventListener('resize', onResize);

    const mobileToggle = mobileTocToggleRef.current;

    const measureAndScroll = () => {
      if (mobileToggle) {
        const trect = mobileToggle.getBoundingClientRect();
        if (mobileToggleOriginalTopRef.current == null) mobileToggleOriginalTopRef.current = trect.top + window.scrollY;
      }

      const hdr = document.querySelector('.header');
      headerHeightRef.current = hdr?.offsetHeight || 84;

      if (mobileToggleOriginalTopRef.current != null) {
        setMobileToggleFixed(window.scrollY >= mobileToggleOriginalTopRef.current - headerHeightRef.current);
      }
    };

    window.addEventListener('scroll', measureAndScroll, { passive: true });
    window.addEventListener('resize', measureAndScroll);
    setTimeout(measureAndScroll, 100);
    return () => {
      window.removeEventListener('scroll', measureAndScroll);
      window.removeEventListener('resize', measureAndScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [dataLoading]);

  const renderBlock = (block, idx) => {
    switch (block.type) {
      case 'text': return <RichText key={idx} content={block.content} />;
      case 'grid': return <div key={idx} className="block-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${block.columnsCount || 2}, minmax(0, 1fr))`, gap: '2rem' }}>{(block.columns || []).map((col, ci) => <div key={ci}>{(col || []).map((sb, si) => renderBlock(sb, `${idx}-${ci}-${si}`))}</div>)}</div>;
      case 'button': return <div key={idx} className="block-button"><Link to={block.url || '#'} className={`btn ${block.style || 'btn-secondary'}`}>{block.label}</Link></div>;
      case 'image': return <div key={idx} className="block-image" style={{ textAlign: block.align || 'center' }}><img src={block.url} alt="" style={{ maxWidth: '100%', borderRadius: block.rounded ? '24px' : '8px' }} />{block.caption && <figcaption>{block.caption}</figcaption>}</div>;
      case 'accordion': return <div key={idx} className="block-accordion">{(block.items || []).map((it, i) => <details key={i} className="premium-accordion"><summary>{it.title}</summary><div className="accordion-content"><RichText content={it.content} /></div></details>)}</div>;
      case 'tabs': return <TabsBlock key={idx} items={block.items} />;
      case 'beforeAfter': return <BeforeAfterBlock key={idx} before={block.beforeUrl} after={block.afterUrl} />;
      case 'stats': return <div key={idx} className="block-stats">{(block.items || []).map((st, i) => <StatCard key={i} stat={st} />)}</div>;
      case 'timeline':
        return (
          <div key={idx} className="block-timeline">
            {(block.items || []).map((step, i) => (
              <div key={i} className="timeline-item">
                <div className="timeline-dot-wrapper">
                  <div className="timeline-dot">{step.icon || (i + 1)}</div>
                </div>
                <div className="timeline-content-card">
                  <div className="timeline-header">
                    <h4 className="timeline-title">{step.title}</h4>
                    {step.tag && <span className="timeline-tag">{step.tag}</span>}
                  </div>
                  <div className="timeline-text"><RichText content={step.content} /></div>
                </div>
              </div>
            ))}
          </div>
        );
      case 'perks':
        return (
          <div key={idx} className="block-perks">
            {(block.items || []).map((perk, i) => (
              <div key={i} className="perk-card">
                <span className="perk-icon" role="img" aria-label="icon">{perk.icon}</span>
                <span className="perk-title">{perk.title}</span>
              </div>
            ))}
          </div>
        );
      case 'downloads':
        return (
          <div key={idx} className="block-downloads">
            {(block.items || []).map((file, i) => {
              const extension = file.url?.split('.').pop()?.split('?')[0]?.toLowerCase() || '';
              const isPdf = extension === 'pdf';
              const fileIcon = isPdf ? '📄' : '📁';
              return (
                <a key={i} href={file.url} target="_blank" rel="noopener noreferrer" className="download-card">
                  <div className="download-info">
                    <div className="download-icon-box">{fileIcon}</div>
                    <div className="download-meta">
                      <span className="download-label">{file.label}</span>
                      <span className="download-size">{file.size}</span>
                    </div>
                  </div>
                  <div className="download-action" title="Télécharger">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  </div>
                </a>
              );
            })}
          </div>
        );
      case 'team':
        return (
          <div key={idx} className="block-team">
            {(block.members || []).map((member, i) => {
              const socialIcons = {
                linkedin: <FiLinkedin />,
                twitter: <FiTwitter />,
                github: <FiGithub />,
                instagram: <FiInstagram />,
                facebook: <FiFacebook />,
                website: <FiGlobe />
              };
              return (
                <div key={i} className="team-member">
                  <div className="member-avatar">
                    {member.avatar ? <img src={member.avatar} alt={member.name} /> : <span>{member.name.charAt(0)}</span>}
                  </div>
                  <div className="member-info">
                    <div className="member-name">{member.name}</div>
                    <div className="member-role">{member.role}</div>
                    {member.bio && <div className="member-bio">{member.bio}</div>}
                    {member.socials && member.socials.length > 0 && (
                      <div className="member-socials">
                        {member.socials.map((soc, sIdx) => (
                          <a key={sIdx} href={soc.url} target="_blank" rel="noopener noreferrer" className="social-link" title={soc.platform}>
                            {socialIcons[soc.platform] || <FiGlobe />}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      case 'map':
        let finalEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(block.address)}&t=&z=${block.zoom || 13}&ie=UTF8&iwloc=&output=embed`;
        let finalExternalLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(block.address)}`;

        if (block.mapUrl) {
          if (block.mapUrl.includes('<iframe')) {
            const srcMatch = block.mapUrl.match(/src="([^"]+)"/);
            if (srcMatch) finalEmbedUrl = srcMatch[1];
          }
          else if (block.mapUrl.startsWith('http')) {
            if (block.mapUrl.includes('google.com/maps/embed') || block.mapUrl.includes('output=embed')) {
              finalEmbedUrl = block.mapUrl;
            } else {
              finalExternalLink = block.mapUrl;
            }
          }
        }

        return (
          <div key={idx} className="block-map premium-map">
            <div className="map-embed-container">
              <iframe
                title="Google Maps"
                src={finalEmbedUrl}
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
              />
              <div className="map-overlay-info">
                <div className="map-info-card">
                  <div className="map-info-icon">📍</div>
                  <div className="map-info-text">
                    <span className="map-address-label">Adresse</span>
                    <span className="map-address-value">{block.address}</span>
                  </div>
                  <a
                    href={finalExternalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="map-route-btn"
                  >
                    Voir sur Google Maps ↗
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      case 'gallery': return <GalleryBlock key={idx} images={block.images} />;
      case 'widget': return <LiveWidget key={idx} block={block} />;
      case 'html': return <HtmlBlock key={idx} block={block} />;
      default: return null;
    }
  };

  const renderSectionContent = (section) => {
    // If blocks exist and are not empty, use them
    if (section.blocks && Array.isArray(section.blocks) && section.blocks.length > 0) {
      return <div className="section-blocks">{section.blocks.map((b, i) => renderBlock(b, i))}</div>;
    }
    // Fallback to legacy content
    return <RichText content={section.content} />;
  };

  if (dataLoading) return <SocieteLoader />;

  return (
    <section id="societe" className="section recrutement-page">
      <div className="section-inner">
        <nav className="breadcrumb" aria-label="Fil d'Ariane">
          <Link to="/">Accueil</Link>
          <span>/</span>
          <span>Société</span>
        </nav>
      </div>
      <div className="services-hero">
        <div className="services-hero-illus" aria-hidden="true" />
        <div className="services-hero-inner">
          <h1 className="services-hero-title"><span className="accent">{heroData.title}</span></h1>
          <p className="services-hero-subtitle">{heroData.subtitle}</p>
          <div
            className="services-cta"
            style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}
          >
            {heroData.buttons.map((btn, idx) => (
              <Link key={idx} className={`btn ${btn.style || 'btn-secondary'}`} to={btn.url}>
                {btn.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="section-inner services-page" ref={containerRef} style={{ position: 'relative', '--toc-top': `${tocTop}px` }}>

        {/* Mobile TOC Toggle */}
        <button
          ref={mobileTocToggleRef}
          className={`services-toc-toggle ${mobileTocOpen ? 'open' : ''}`}
          aria-expanded={mobileTocOpen}
          aria-controls="recrutement-toc-mobile"
          onClick={() => setMobileTocOpen((o) => !o)}
          type="button"
          style={mobileToggleFixed ? { position: 'fixed', top: `${headerHeightRef.current}px`, left: 0, right: 0, margin: 0, borderRadius: 0, zIndex: 90 } : { top: mobileToggleTop }}
        >
          <span className="hamburger" aria-hidden="true"><span /><span /><span /></span>
          <span className="label">Sommaire Société</span>
          <span className="plus" aria-hidden="true">{mobileTocOpen ? '−' : '+'}</span>
        </button>
        <div className={`services-toc-backdrop ${mobileTocOpen ? 'open' : ''}`} onClick={() => setMobileTocOpen(false)} />

        {/* Mobile TOC Content */}
        <div id="recrutement-toc-mobile" className={`services-toc-mobile ${mobileTocOpen ? 'open' : ''}`} role="dialog" aria-modal="true">
          <div className="services-toc-mobile-header">
            <span>Sommaire</span>
            <button type="button" className="close" onClick={() => setMobileTocOpen(false)} aria-label="Fermer">×</button>
          </div>
          <nav className="services-toc-mobile-nav" onClick={() => setMobileTocOpen(false)}>
            {categories.map(cat => (
              <React.Fragment key={cat.id}>
                <a className={`services-toc-group-link ${activeId === cat.slug ? 'active' : ''}`} href={`#${cat.slug}`}>
                  {cat.title}
                </a>
                {sections
                  .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                  .filter(s => s.is_published !== false)
                  .map(s => (
                    <ul key={s.id} className="toc-list">
                      <li className="toc-item">
                        <a className={activeId === s.slug ? 'active' : ''} href={`#${s.slug}`}>
                          {s.title}
                        </a>
                      </li>
                    </ul>
                  ))}
              </React.Fragment>
            ))}
          </nav>
        </div>

        {/* Desktop TOC */}
        <div style={{ position: 'relative', overflow: 'visible' }}>
          <ModernToc
            data={tocData}
            title="Sommaire"
            ariaLabel="Sommaire Société"
            boundarySelectors={['.map-banner', 'footer']}
          />
        </div>

        {/* Main Content */}
        <div className="services-content">
          {dataLoading ? (
            <SocieteLoader />
          ) : categories.length === 0 && sections.length === 0 ? (
            <div className="alert alert-info">
              <p>Aucun contenu n'est disponible pour le moment.</p>
              <small>Si vous êtes administrateur, configurez les catégories et les sections dans "Admin &gt; Société".</small>
            </div>
          ) : (
            <>
              {categories.map(cat => {
                const catSections = sections
                  .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                  .filter(s => s.is_published === true)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                return (
                  <div key={cat.id} className="recrutement-category-block">
                    <div id={cat.slug} style={{ scrollMarginTop: '100px' }} />

                    {/* Render sections inside this category */}
                    {catSections.length > 0 ? (
                      catSections.map((section, index) => (
                        <section key={section.id} id={section.slug} style={{ marginTop: '1rem', scrollMarginTop: '100px' }}>
                          <article className="recrutement-job-card">

                            <div className="job-body">
                              {renderSectionContent(section)}
                            </div>
                          </article>
                        </section>
                      ))
                    ) : (
                      null
                    )}

                    <div style={{ height: '2rem' }} />
                  </div>
                )
              })}

              {/* Fallback for orphans - Optional, usually hidden in production to avoid residues */}
              {(() => {
                // If you want to see orphans during development, keep this. Else, return null.
                const knownCatIds = categories.map(c => c.id);
                const orphans = sections.filter(s => {
                  const catId = typeof s.category === 'object' ? s.category?.id : s.category;
                  return (!catId || !knownCatIds.includes(catId)) && s.is_published === true;
                });

                if (orphans.length === 0) return null;

                return (
                  <div className="recrutement-orphans-block">
                    <h3 style={{ opacity: 0.5, borderBottom: '1px dashed' }}>Autres informations (Orphelins)</h3>
                    {orphans.map(section => (
                      <section key={section.id} id={section.slug} style={{ marginTop: '1rem', scrollMarginTop: '100px', opacity: 0.7 }}>
                        <article className="recrutement-job-card">
                          <div className="job-body">
                            {renderSectionContent(section)}
                          </div>
                        </article>
                      </section>
                    ))}
                  </div>
                );
              })()}
            </>
          )}

          <div id="societe-end" ref={endRef} style={{ height: 1 }} />
        </div>
      </div>
    </section>
  );
}
